import { Box, Grow, Stack } from "@mui/material";
import { forwardRef } from "react";

import AppButton from "@/common/components/Button/AppButton";
import AppIconButton from "@/common/components/Button/AppIconButton";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppPopperContent from "@/common/components/Popper/AppPopperContent";
import AppTypography from "@/common/components/Text/AppTypography";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useOnboardingContext } from "@/common/contexts/onboarding";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { appCommonConfig } from "@/config/config";

import { OnboardingProgressIndicator } from "../OnboardingProgressIndicator";
import { OnboardingStepChecklistCompact } from "../Step/OnboardingStepChecklistCompact";
import { OnboardingPopperMode } from "./OnboardingPopper";

interface Props {
  // /** Passed from outside as popper might be rendered outside of the context provider via portal. */
  // onboardingContext: OnboardingContextValue;
  onClose: () => void;
}

export const OnboardingPopperContent = forwardRef<HTMLDivElement, Props>(
  function OnboardingPopperContent({ onClose }: Props, ref) {
    const history = useAppHistory();
    const profile = useUserProfile();
    const onboardingContext = useOnboardingContext();

    const closeButton = (
      <AppTooltip title='Close' isDefaultEnterDelays>
        <AppIconButton onClick={onClose}>
          <AppIcon of='close' />
        </AppIconButton>
      </AppTooltip>
    );

    const expandButton = (
      <AppTooltip title='Expand' isDefaultEnterDelays>
        <AppIconButton
          onClick={() => {
            onboardingContext.popper.setMode(OnboardingPopperMode.Expanded);
          }}
        >
          <AppIcon of='expand' />
        </AppIconButton>
      </AppTooltip>
    );

    const collapseButton = (
      <AppTooltip title='Collapse' isDefaultEnterDelays>
        <AppIconButton
          onClick={() => {
            onboardingContext.popper.setMode(OnboardingPopperMode.Collapsed);
          }}
        >
          <AppIcon of='collapse' />
        </AppIconButton>
      </AppTooltip>
    );

    const continueButton = (
      <AppButton
        variant='contained'
        size='medium'
        color='secondary'
        fullWidth
        endIcon={<AppIcon of='arrowForward' />}
        onClick={async () => {
          history.push(ROUTE_PATH.ONBOARDING_VIEW_STEPS());
          onClose();
        }}
      >
        Continue Onboarding
      </AppButton>
    );

    return (
      <AppPopperContent
        ref={ref}
        sx={{
          width: 400,
          maxWidth: { xxs: "95vw", md: 400 },
          maxHeight: { xxs: "95vh", md: 600 },
          p: 0,
          borderRadius: (th) => th.shapeCustom.borderRadiusCardLarge,
        }}
        isUseDefaultStyles
        paperProps={{ elevation: 3 }}
      >
        {onboardingContext.popper.mode === OnboardingPopperMode.Collapsed && (
          <Grow in={true} timeout={300}>
            <Box>
              {/* Body */}
              <Box sx={{ p: (th) => th.spacing(1.5, 2) }}>
                <Stack direction='row' sx={{ alignItems: "center", gap: 6 }}>
                  {continueButton}

                  <Stack direction='row' sx={{ alignItems: "center", gap: 0, ml: "auto" }}>
                    {expandButton}

                    {closeButton}
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Grow>
        )}

        {onboardingContext.popper.mode === OnboardingPopperMode.Expanded && (
          <Grow in={true} timeout={300}>
            <Box>
              {/* Header */}
              <Box
                sx={{
                  p: (th) => th.spacing(2, 2),
                  borderBottom: (th) => `1px solid ${th.palette.divider}`,
                }}
              >
                <Stack direction='row' sx={{ alignItems: "center", gap: 1 }}>
                  <AppTypography variant='h5'>Onboarding flow</AppTypography>

                  <Stack direction='row' sx={{ alignItems: "center", gap: 0, ml: "auto" }}>
                    {collapseButton}

                    {closeButton}
                  </Stack>
                </Stack>
              </Box>

              {/* Body */}
              <Box sx={{ p: (th) => th.spacing(3, 2, 5, 2) }}>
                <AppTypography variant='h5'>
                  Hello
                  {profile?.personName?.firstName ? <>, {profile?.personName?.firstName}</> : null}!
                  👋
                </AppTypography>
                <AppTypography variant='body1' color='textSecondary'>
                  Your gonna be a {appCommonConfig.appNameShort} superstar!
                </AppTypography>

                <OnboardingProgressIndicator
                  sx={{ mt: 3 }}
                  withPercentageText
                  withTooltip={false}
                />

                <OnboardingStepChecklistCompact
                  sx={{ mt: 4, maxHeight: 230, overflowY: "auto", scrollbarWidth: "thin" }}
                />

                <Box sx={{ mt: 2 }}>{continueButton}</Box>
              </Box>
            </Box>
          </Grow>
        )}
      </AppPopperContent>
    );
  },
);
